import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  CircularProgress,
  Alert,
  useTheme,
  Chip,
} from '@mui/material';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { useUsers } from '../../hooks/user/useUsers';
import { useUserActions } from '../../hooks/user/useUserActions';
import { User } from '../../types/user';
import UserForm from './components/UserForm';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import EmptyStateIcon from '../../assets/images/emptyState-illus.svg';
import { AUTH_CONSTANTS, ROLE_DISPLAY_MAPPING } from '../../config/constants';

const UsersView = () => {
  const { users, isLoading, error, refetch } = useUsers();
  const { createUser, updateUser, deleteUser } = useUserActions();
  const theme = useTheme();

  // UI State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    cognitoId: '',
  });

  // Notification State
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const showNotification = (message: string, severity: SEVERITY) => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setIsFormOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (cognitoId: string) => {
    setConfirmDialog({
      open: true,
      cognitoId,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteUser(confirmDialog.cognitoId);
      await refetch();
      showNotification('User deleted successfully', SEVERITY.SUCCESS);
    } catch (error) {
      showNotification('Failed to delete user', SEVERITY.ERROR);
    } finally {
      setConfirmDialog({ open: false, cognitoId: '' });
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      if (selectedUser) {
        await updateUser(selectedUser.cognitoId, data);
        showNotification('User updated successfully', SEVERITY.SUCCESS);
      } else {
        await createUser(data);
        showNotification(
          'User created successfully. An invitation email has been sent.',
          SEVERITY.SUCCESS
        );
      }
      await refetch();
      setIsFormOpen(false);
      setSelectedUser(null);
    } catch (error) {
      showNotification(`Failed to ${selectedUser ? 'update' : 'create'} user`, SEVERITY.ERROR);
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box className="w-full 2xl:w-3/4 mx-auto mt-10">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>
            Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user access and permissions
          </Typography>
        </Box>
        {users.length > 0 && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Plus size={20} />}
            onClick={handleAdd}
          >
            Add User
          </Button>
        )}
      </Box>

      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : !users.length ? (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, py: 6 }}
          >
            <img className="w-28 mx-auto" src={EmptyStateIcon} alt="No data found" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              No users found
            </Typography>
            <Button
              variant="contained"
              disableElevation
              startIcon={<Plus size={20} />}
              onClick={handleAdd}
              sx={{ mt: 3 }}
            >
              Add User
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Email</TableCell>
                  <TableCell sx={{ py: 1, color: theme.palette.text.secondary }}>Mobile</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary }}>Role</TableCell>
                  <TableCell
                    align="right"
                    sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(user => (
                  <TableRow
                    key={user.cognitoId}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell sx={{ px: 3, fontSize: 16 }}>
                      {' '}
                      {user.firstName
                        ? `${user.firstName} ${user.lastName || ''}`.trim()
                        : 'Not Provided'}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.mobileNo || 'Not Provided'}</TableCell>
                    <TableCell>
                      <Chip
                        label={ROLE_DISPLAY_MAPPING[user.role]}
                        size="small"
                        color={user.role === AUTH_CONSTANTS.ROLES.ADMIN ? 'primary' : 'success'}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ px: 3 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(user)} sx={{ mr: 2 }}>
                          <Edit2 size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(user.cognitoId)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <UserForm
          user={selectedUser || undefined}
          onSubmit={handleSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      </Dialog>

      <ConfirmationDialog
        open={confirmDialog.open}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, cognitoId: '' })}
      />

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default UsersView;
